import React, { useState, useEffect, useRef } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import WorkoutTile from '../workoutTile';
import SearchInput from '../../common/Input/SearchInput';
import Pagination from '../../common/Pagination/Pagination';
import SelectMenu from '../../common/SelectMenu';
import { PER_PAGE } from '../../../utils/constants';
import { getWorkouts } from '../../../api/healthAPI';

const WorkoutSelectionModal = ({ isOpen, onClose, onSelectWorkouts, preSelectedWorkouts = [] }) => {
  const [workouts, setWorkouts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedPerPage, setSelectedPerPage] = useState(PER_PAGE[0]);
  const [searchTerm, setSearchTerm] = useState('');
  // Track selected workout objects by id for cross-page persistence
  const [selectedMap, setSelectedMap] = useState({});
  const modalRef = useRef(null);

  // Initialize selected map from pre-selected workouts when modal opens
  useEffect(() => {
    if (isOpen) {
      const map = {};
      preSelectedWorkouts.forEach((w) => {
        const id = w._id || w.workout_id;
        if (id) map[id] = w;
      });
      setSelectedMap(map);
    }
  }, [isOpen, preSelectedWorkouts]);

  useEffect(() => {
    if (isOpen) {
      fetchWorkouts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, selectedPerPage, isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handler = setTimeout(() => {
      setCurrentPage(1);
      fetchWorkouts();
    }, 400);
    return () => clearTimeout(handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const fetchWorkouts = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: selectedPerPage?.value || 10,
        search: searchTerm || undefined
      };
      const response = await getWorkouts(params);

      if (response?.meta?.code === 1 && response?.data) {
        setWorkouts(response.data);
        setTotalCount(response?.meta?.total || 0);
      } else {
        setWorkouts([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error('Error fetching workouts:', error);
      setWorkouts([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleWorkout = (workout) => {
    setSelectedMap((prev) => {
      const next = { ...prev };
      if (next[workout._id]) {
        delete next[workout._id];
      } else {
        next[workout._id] = workout;
      }
      return next;
    });
  };

  const handleConfirm = () => {
    onSelectWorkouts(Object.values(selectedMap));
    onClose();
  };

  const handleClose = () => {
    setSearchTerm('');
    setCurrentPage(1);
    onClose();
  };

  const selectedCount = Object.keys(selectedMap).length;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={handleClose}
        />

        {/* Modal panel */}
        <div
          ref={modalRef}
          className="inline-block align-bottom bg-white dark:bg-shoorah-darkBgTabColor rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full"
        >
          {/* Header */}
          <div className="bg-white dark:bg-shoorah-darkBgTabColor px-6 py-4 border-b border-gray-200 dark:border-shoorah-darkBorder">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Select Available Options
              </h3>
              <button
                onClick={handleClose}
                className="text-gray-400 hover:text-gray-500 focus:outline-none"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Search and Filter */}
          <div className="bg-white dark:bg-shoorah-darkBgTabColor px-6 py-4 border-b border-gray-200 dark:border-shoorah-darkBorder">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-2 flex-1">
                <SearchInput
                  id="searchKey"
                  name="searchKey"
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search"
                  className="w-full"
                />
                <div className="w-[90px] shrink-0">
                  <SelectMenu
                    menuList={PER_PAGE}
                    showLabel={false}
                    defaultSelected={selectedPerPage}
                    setSelectedMenu={(option) => {
                      setSelectedPerPage(option);
                      setCurrentPage(1);
                    }}
                  />
                </div>
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400 shrink-0">
                {selectedCount} selected
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="bg-white dark:bg-shoorah-darkBgTabColor px-6 py-4 max-h-[500px] overflow-y-auto">
            {loading && (
              <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
              </div>
            )}

            {!loading && workouts.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {workouts.map((workout) => (
                  <WorkoutTile
                    key={workout._id}
                    image={workout.cover_image_url}
                    title={workout.workout_name}
                    usageCount={workout.usage_count || 0}
                    badgeText={workout.difficulty || 'Workout'}
                    selectionMode={true}
                    isSelected={!!selectedMap[workout._id]}
                    onSelect={() => handleToggleWorkout(workout)}
                  />
                ))}
              </div>
            )}

            {!loading && workouts.length === 0 && (
              <div className="text-center py-12">
                <p className="text-gray-500 dark:text-gray-400">No workouts found</p>
              </div>
            )}
          </div>

          {/* Pagination */}
          {!loading && workouts.length > 0 && (
            <div className="bg-white dark:bg-shoorah-darkBgTabColor px-6 py-4 border-t border-gray-200 dark:border-shoorah-darkBorder">
              <Pagination
                currentPage={currentPage}
                totalCount={totalCount}
                pageSize={selectedPerPage?.value || 10}
                onPageChange={(page) => setCurrentPage(page)}
                disabled={loading}
              />
            </div>
          )}

          {/* Footer */}
          <div className="bg-gray-50 dark:bg-shoorah-darkBgColor px-6 py-4 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={handleClose}
              className="px-6 py-2 border border-gray-300 dark:border-gray-600 rounded-full text-sm font-medium text-gray-700 dark:text-white hover:bg-gray-50 dark:hover:bg-shoorah-darkBgTabColor"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              className="btn-primary"
            >
              Add Selected ({selectedCount})
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkoutSelectionModal;
